/**
 * Public location display for event cards and the event detail page.
 *
 * Prefer structured `event.Address` (manual programs, address overrides).
 * Fall back to parsing `event.Location` for typical scraped rows.
 */

export function parseLocation(location) {
  if (isPlaceholderLocation(location)) {
    return { venue: 'TBD', address: null };
  }

  const parts = location.split(/[•,]/).map((p) => p.trim()).filter(Boolean);

  if (parts.length >= 2) {
    return {
      venue: parts[0],
      address: parts.slice(1).join(', '),
    };
  }

  if (/\d/.test(location) || location.toLowerCase().includes('st') || location.toLowerCase().includes('ave')) {
    return { venue: null, address: location };
  }

  return { venue: location, address: null };
}

const PLACEHOLDER_LOCATIONS = new Set([
  '',
  'location not found',
  'no location',
  'unknown location',
  'tbd',
]);

const CITY_ALIASES = {
  'carnel by the sea': 'Carmel-by-the-Sea',
  'mounain view': 'Mountain View',
  'mountain view': 'Mountain View',
  'san francosco': 'San Francisco',
  'san fransisco': 'San Francisco',
  'san francisco': 'San Francisco',
  'san jos': 'San Jose',
  'san jose': 'San Jose',
};

const KNOWN_CITY_NAMES = [
  'Alameda', 'Antioch', 'Arcata', 'Berkeley', 'Brentwood', 'Burlingame', 'Campbell', 'Capitola',
  'Carmel-by-the-Sea', 'Carmichael', 'Caspar', 'Concord', 'Danville', 'Davis', 'Dublin',
  'Elk', 'Emeryville', 'Eureka', 'Foster City', 'Fremont', 'Hayward', 'Larkspur', 'Livermore', 'Los Altos',
  'Mill Valley', 'Millbrae', 'Mountain View', 'Nevada City', 'Newark', 'North Fork',
  'Novato', 'Oakland', 'Orinda', 'Pacific Grove', 'Palo Alto', 'Point Reyes Station',
  'Redwood City', 'Richmond', 'Rohnert Park', 'Sacramento', 'San Carlos', 'San Francisco',
  'San Jose', 'San Mateo', 'San Rafael', 'Santa Clara', 'Santa Cruz', 'Santa Rosa', 'Sausalito',
  'Scotts Valley', 'Sebastopol', 'Sonoma', 'Stanford', 'Sunnyvale', 'Walnut Creek',
  'West Sacramento',
];

function cityLookupKey(value = '') {
  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.]/g, '')
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

const KNOWN_CITIES = KNOWN_CITY_NAMES.reduce((lookup, city) => {
  lookup[cityLookupKey(city)] = city;
  return lookup;
}, { ...CITY_ALIASES });

const APPROVED_LOCATION_OVERRIDES = [
  {
    city: 'Palo Alto',
    venue: 'Cubberley Community Center',
    address: 'Room H6, 4000 Middlefield Road, Palo Alto, CA 94303',
    formattedAddress:
      'Cubberley Community Center, Room H6, 4000 Middlefield Road, Palo Alto, CA 94303',
    aliases: ['Cubberley Community Center', '4000 Middlefield Road'],
  },
  {
    city: 'Carmel-by-the-Sea',
    venue: 'Sunset Center',
    address: 'San Carlos Street at Ninth Avenue, Carmel-by-the-Sea, CA',
    formattedAddress:
      'Sunset Center, San Carlos Street at Ninth Avenue, Carmel-by-the-Sea, CA',
    aliases: ['Sunset Center', 'San Carlos Street at Ninth Avenue', 'San Carlos St at Ninth Ave'],
  },
  {
    city: 'Stanford',
    venue: 'Roble Arts Gym Room 113',
    address: '375 Santa Teresa Street, Stanford, CA 94305',
    formattedAddress:
      'Roble Arts Gym Room 113, 375 Santa Teresa Street, Stanford, CA 94305',
    aliases: ['Roble Arts Gym Room 113', '375 Santa Teresa Street', '375 Santa Teresa St'],
  },
];

function locationLookupKey(value = '') {
  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function approvedLocationOverride(value = '') {
  const key = locationLookupKey(value);
  if (!key) return null;

  return APPROVED_LOCATION_OVERRIDES.find((override) =>
    override.aliases.some((alias) => key.includes(locationLookupKey(alias)))
  ) || null;
}

export function isPlaceholderLocation(value = '') {
  return PLACEHOLDER_LOCATIONS.has(String(value || '').trim().toLowerCase());
}

export function extractCityFromLocation(locationStr = '') {
  if (isPlaceholderLocation(locationStr)) return null;
  const parts = String(locationStr || '').split(',').map((part) => part.trim()).filter(Boolean);
  for (let i = 0; i < parts.length; i++) {
    if (/^(?:CA|California)(?:\s+\d{5}(?:-\d{4})?)?$/i.test(parts[i])) {
      if (i > 0) return normalizeCityName(parts[i - 1]);
    }
  }

  const approvedOverride = approvedLocationOverride(locationStr);
  if (approvedOverride) return approvedOverride.city;

  // A single value belongs in the filter only when it is a recognized city.
  if (parts.length === 1) return KNOWN_CITIES[cityLookupKey(parts[0])] || null;
  return null;
}

export function normalizeCityName(city = '') {
  const value = String(city).trim();
  return KNOWN_CITIES[cityLookupKey(value)] || value;
}

function normalizeLocationText(value = '') {
  return String(value)
    .replace(/Mounain View/gi, 'Mountain View')
    .replace(/San Francosco/gi, 'San Francisco')
    .replace(/San Fransisco/gi, 'San Francisco')
    .replace(/San Jos(?=\s*(?:,|$))/gi, 'San Jose');
}

function escapeRegex(s = '') {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function isCityStateOnlyLocation(fullLocation = '', city = '') {
  const full = (fullLocation || '').trim().replace(/\s+/g, ' ');
  const c = (city || '').trim().replace(/\s+/g, ' ');
  if (!full || !c) return false;
  if (full.localeCompare(c, undefined, { sensitivity: 'accent' }) === 0) return true;
  const re = new RegExp(`^${escapeRegex(c)}\\s*,\\s*[A-Za-z]{2}(?:\\s+\\d{5}(?:-\\d{4})?)?$`, 'i');
  return re.test(full);
}

function trimOrNull(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

export function formatStreetAddress(addr) {
  if (!addr || typeof addr !== 'object') return '';
  const stateZip = [addr.state, addr.postalCode].filter(Boolean).join(' ');
  return [addr.addressLine1, addr.addressLine2, normalizeCityName(addr.city), stateZip]
    .map((part) => trimOrNull(part))
    .filter(Boolean)
    .join(', ');
}

export function formatVenueLocationFromAddress(addr) {
  if (!addr || typeof addr !== 'object') return '';
  const venue = trimOrNull(addr.venue);
  const street = formatStreetAddress(addr);
  return [venue, street].filter(Boolean).join(', ');
}

export function cityFromEvent(event) {
  const fromAddress = trimOrNull(event?.Address?.city);
  if (fromAddress) return normalizeCityName(fromAddress);
  return extractCityFromLocation(event?.Location);
}

export function resolveEventLocation(event) {
  const location = event?.Location || '';
  const structured = event?.Address && typeof event.Address === 'object' ? event.Address : null;
  const isTbd = isPlaceholderLocation(location) && !trimOrNull(structured?.venue) && !trimOrNull(structured?.addressLine1);

  if (isTbd) {
    return {
      isTbd: true,
      city: null,
      venue: 'TBD',
      address: null,
      mapsQuery: '',
      cardPrimary: null,
      cardSecondary: null,
      showSecondary: false,
    };
  }

  if (structured) {
    const venue = trimOrNull(structured.venue);
    const address = formatStreetAddress(structured) || null;
    const city = trimOrNull(structured.city)
      ? normalizeCityName(structured.city)
      : extractCityFromLocation(location);
    const composed = formatVenueLocationFromAddress(structured);
    const mapsQuery =
      normalizeLocationText(trimOrNull(structured.mapsQuery) || '') ||
      normalizeLocationText(trimOrNull(structured.formattedAddress) || '') ||
      composed ||
      location;
    const hasStreet = Boolean(trimOrNull(structured.addressLine1));
    const cardSecondary = composed || location;
    const showSecondary = hasStreet || (Boolean(city) && !isCityStateOnlyLocation(cardSecondary, city));

    return {
      isTbd: false,
      city,
      venue: venue || parseLocation(location).venue,
      address,
      mapsQuery,
      cardPrimary: city || cardSecondary,
      cardSecondary,
      showSecondary,
    };
  }

  const approvedOverride = approvedLocationOverride(location);
  if (approvedOverride) {
    return {
      isTbd: false,
      city: approvedOverride.city,
      venue: approvedOverride.venue,
      address: approvedOverride.address,
      mapsQuery: approvedOverride.formattedAddress,
      cardPrimary: approvedOverride.city,
      cardSecondary: approvedOverride.formattedAddress,
      showSecondary: true,
    };
  }

  const parsed = parseLocation(location);
  const city = extractCityFromLocation(location);
  const showSecondary = Boolean(city) && !isCityStateOnlyLocation(location, city);

  return {
    isTbd: false,
    city,
    venue: parsed.venue,
    address: parsed.address,
    mapsQuery: location,
    cardPrimary: city || location,
    cardSecondary: location,
    showSecondary,
  };
}
